import { useState, useEffect } from "react";
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import { alpha } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Zoom from "@mui/material/Zoom";

// Material Icons
import BoltIcon from '@mui/icons-material/Bolt';
import BarChartIcon from '@mui/icons-material/BarChart';
import SpeedIcon from '@mui/icons-material/Speed';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';

// RasPm Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import ErrorBoundary from "components/ErrorBoundary";

// Custom components
// import AddTransaction from "../AddTransaction";

// Recharts components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

// Services
import { RequestService } from "services/RequestService";

function TableChartView({ powerStationId, startDate, endDate }) {
  const [chartData, setChartData] = useState([]);
  const [stats, setStats] = useState({
    totalPowerGenerator: 0,
    totalExpectedValue: 0,
    totalProductionRate: 0,
  });
  const [loading, setLoading] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const getDatesInRange = (start, end) => {
    const dates = [];
    const currentDate = new Date(start);
    const endDate = new Date(end);

    while (currentDate <= endDate) {
      dates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  };

  const fetchDayData = async (date) => {
    try {
      const response = await RequestService.get(
        `PowerStation/table?PowerStationId=${powerStationId}&Date=${date}`
      );
      if (response.data && !response.data.error) {
        const { data } = response.data;
        
        // Calculate daily totals
        let dailyActual = 0;
        let dailyExpected = 0;

        data.stGroups.forEach(stGroup => {
          stGroup.gtUnits.forEach(unit => {
            dailyActual += unit.gtValue;
            dailyExpected += unit.expectedStValue;
          });
        });

        return {
          date,
          "التوليد الفعلي": dailyActual,
          "التوليد المتوقع": dailyExpected,
          "معدل الإنتاج": dailyExpected > 0 ? (dailyActual / dailyExpected) * 100 : 0,
        };
      }
      return null;
    } catch (error) {
      console.error(`Error fetching data for ${date}:`, error);
      return null;
    }
  };

  const fetchChartData = async () => {
    setLoading(true);
    try {
      const dates = getDatesInRange(startDate, endDate);
      const dailyDataPromises = dates.map(date => fetchDayData(date));
      const dailyDataResults = await Promise.all(dailyDataPromises);
      
      // Filter out null results and sort by date
      const validData = dailyDataResults
        .filter(data => data !== null)
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      setChartData(validData);

      // Calculate overall statistics
      if (validData.length > 0) {
        const totalActual = validData.reduce((sum, day) => sum + day["التوليد الفعلي"], 0);
        const totalExpected = validData.reduce((sum, day) => sum + day["التوليد المتوقع"], 0);
        const avgProductionRate = totalExpected > 0 ? (totalActual / totalExpected) * 100 : 0;

        setStats({
          totalPowerGenerator: totalActual.toFixed(2),
          totalExpectedValue: totalExpected.toFixed(2),
          totalProductionRate: avgProductionRate.toFixed(2),
        });
      }
    } catch (error) {
      console.error("Error fetching chart data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (powerStationId && startDate && endDate) {
      fetchChartData();
    }
  }, [powerStationId, startDate, endDate]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{ 
          backgroundColor: 'white', 
          padding: '10px', 
          border: '1px solid #ccc',
          borderRadius: '8px',
          direction: 'rtl'
        }}>
          <p style={{ margin: '0', fontWeight: 'bold' }}>{formatDate(data.date)}</p>
          <p style={{ margin: '0' }}>{`التوليد الفعلي: ${data["التوليد الفعلي"].toFixed(2)} ميجاوات`}</p>
          <p style={{ margin: '0' }}>{`التوليد المتوقع: ${data["التوليد المتوقع"].toFixed(2)} ميجاوات`}</p>
          <p style={{ margin: '0' }}>{`معدل الإنتاج: ${data["معدل الإنتاج"].toFixed(2)}%`}</p>
        </div>
      );
    }
    return null;
  };

  CustomTooltip.propTypes = {
    active: PropTypes.bool,
    payload: PropTypes.array,
  };

  const handleAddClick = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsAddModalOpen(false);
  };

  const handleTransactionAdded = () => {
    setIsAddModalOpen(false);
    handleRefresh();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Add your refresh logic here
    setTimeout(() => {
      setIsRefreshing(false);
      fetchChartData();
    }, 1000);
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" alignItems="center" p={3}>
        <MDTypography variant="h6" fontWeight="medium">
          جاري تحميل البيانات...
        </MDTypography>
      </MDBox>
    );
  }

  return (
    <ErrorBoundary>
      <Card 
        sx={{ 
          height: "100%",
          overflow: "hidden",
          position: "relative",
          transition: "all 0.2s",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: (theme) => `0 8px 24px -4px ${alpha(theme.palette.info.main, 0.15)}`,
          },
        }}
      >
        <MDBox p={3} pb={0}>
          <MDBox>
            <MDTypography variant="h6" fontWeight="medium" color="text" textTransform="capitalize">
              إجمالي التوليد
            </MDTypography>
            <MDTypography variant="button" color="text" fontWeight="regular" opacity={0.8}>
              إجماليالتوليد الفعلي والمتوقع
            </MDTypography>
          </MDBox>
        </MDBox>
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} md={4}>
            <Card sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <MDBox p={3} display="flex" alignItems="center">
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="4rem"
                  height="4rem"
                  variant="gradient"
                  bgColor="info"
                  color="white"
                  shadow="md"
                  borderRadius="xl"
                  mr={2}
                >
                  <BoltIcon fontSize="large" />
                </MDBox>
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" color="text">
                    إجمالي التوليد
                  </MDTypography>
                  <MDTypography variant="h4" fontWeight="bold">
                    {stats.totalPowerGenerator} ميجاوات
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <MDBox p={3} display="flex" alignItems="center">
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="4rem"
                  height="4rem"
                  variant="gradient"
                  bgColor="success"
                  color="white"
                  shadow="md"
                  borderRadius="xl"
                  mr={2}
                >
                  <BarChartIcon fontSize="large" />
                </MDBox>
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" color="text">
                    التوليد المتوقع
                  </MDTypography>
                  <MDTypography variant="h4" fontWeight="bold">
                    {stats.totalExpectedValue} ميجاوات
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card sx={{ overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column' }}>
              <MDBox p={3} display="flex" alignItems="center">
                <MDBox
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="4rem"
                  height="4rem"
                  variant="gradient"
                  bgColor="warning"
                  color="white"
                  shadow="md"
                  borderRadius="xl"
                  mr={2}
                >
                  <SpeedIcon fontSize="large" />
                </MDBox>
                <MDBox>
                  <MDTypography variant="h6" fontWeight="medium" color="text">
                    معدل الإنتاج
                  </MDTypography>
                  <MDTypography variant="h4" fontWeight="bold">
                    {stats.totalProductionRate}%
                  </MDTypography>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card 
              sx={{ 
                height: "100%",
                maxHeight: "500px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <MDBox p={3} flex={1}>
                <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  {/* <MDButton
                    variant="gradient"
                    color="info"
                    size="small"
                    onClick={handleAddClick}
                    startIcon={<AddIcon />}
                    sx={{ ml: 2 }}
                  >
                    إضافة معاملة / Add Transaction
                  </MDButton> */}
                  <MDButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={handleRefresh}
                    startIcon={
                      <RefreshIcon
                        sx={{
                          animation: isRefreshing ? "spin 1s linear infinite" : "none",
                          "@keyframes spin": {
                            "0%": { transform: "rotate(0deg)" },
                            "100%": { transform: "rotate(360deg)" },
                          },
                        }}
                      />
                    }
                  >
                    تحديث / Refresh
                  </MDButton>
                </MDBox>
                <MDBox mb={3} display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" fontWeight="medium">
                    توليد الوحدات
                  </MDTypography>
                  <MDBox display="flex" gap={2}>
                  </MDBox>
                </MDBox>
                <MDBox 
                  sx={{ 
                    height: "400px",
                    width: "100%",
                    overflow: "hidden",
                  }}
                >
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={chartData}
                      margin={{
                        top: 20,
                        right: 30,
                        left: 20,
                        bottom: 65,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date"
                        angle={-45}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 12 }}
                        padding={{ left: 20, right: 20 }}
                      />
                      <YAxis 
                        label={{ 
                          value: 'ميجاوات', 
                          angle: -90, 
                          position: 'insideLeft',
                          dx: -10
                        }}
                        tick={{ dx: -35 }}
                        tickMargin={10}
                      />
                      <RechartsTooltip 
                        content={<CustomTooltip />}
                        cursor={{ fill: alpha('#000', 0.05) }}
                      />
                      <Legend 
                        wrapperStyle={{ 
                          paddingTop: '20px',
                          marginBottom: '-10px'
                        }}
                      />
                      <Bar 
                        dataKey="التوليد الفعلي" 
                        fill="#2196f3"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar 
                        dataKey="التوليد المتوقع" 
                        fill="#4caf50"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </Card>

     
    </ErrorBoundary>
  );
}

TableChartView.propTypes = {
  powerStationId: PropTypes.string.isRequired,
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
};

export default TableChartView;
